import { Collapse, DialogModal, useModal } from '@shape-ds/components';
import { getIsAdvancingStage } from 'components/StatusSelectWithButton/StatusSelectWithButton';
import {
  CollapseActionButton,
  CollapseTitle,
  CollapseTitleProps,
} from 'pages/Asset/tabs/Cases/components/Collapse/CollapseTitle';
import { caseStatus, CaseStatus } from 'queries/cases/types';
import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getIsPreviousStepFilled, requiredFieldsByStage } from './CaseFormStep.helper';
import { capitalize } from 'components/DownloadCSV/csvUtils';
import { isStepAvailable } from './CaseStepHelper';
import { useFeatureFlag } from '@shape/feature-flag';
import { useLocation } from 'react-router-dom';

export type FormComponentProps = {
  isStepRequired: boolean;
  isStepAble: boolean;
  isAdvancingStage: boolean;
  isCurrentStageSelected: boolean;
  isPreviousStepFilled: boolean;
  onSubmit: (e?: React.BaseSyntheticEvent) => void;
};

export type FormStepProps = {
  stepName: CaseStatus;
  formComponent: React.FC<FormComponentProps>;
  onSubmit: (e?: React.BaseSyntheticEvent) => void;
  title?: React.ReactNode;
  isFormDisabled: boolean;
} & Omit<CollapseTitleProps, 'title' | 'indicator'>;

export const CaseStatusEnum: Record<CaseStatus, number> = {
  diagnosis: 1,
  actions: 2,
  feedback: 3,
  closed: 4,
  canceled: 0,
};
const CaseFormStep = ({
  stepName,
  formComponent,
  button,
  onSubmit,
  isFormDisabled,
  ...props
}: FormStepProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const {
    formState: { defaultValues },
    getValues,
    setValue,
  } = useFormContext();

  const caseId = defaultValues?.id;
  const currentStatus = defaultValues?.status || caseStatus.diagnosis;
  const [isOpen, setIsOpen] = useState(currentStatus === stepName);
  const { openModal } = useModal();
  const nextStatus = getValues('status');
  const isDemoOTCEnabled = useFeatureFlag('demo-OTC');
  const isStepAble =
    isDemoOTCEnabled || (!isFormDisabled && isStepAvailable(currentStatus, stepName));
  const isAdvancingStage = getIsAdvancingStage(nextStatus, stepName);

  const isStepRequired = isStepAble && (isAdvancingStage || stepName === caseStatus.diagnosis);
  const isPreviousStepFilled = getIsPreviousStepFilled(getValues, stepName);

  useEffect(() => {
    const lastEditedField = location.state?.lastEditedField;
    const stageFields = requiredFieldsByStage[stepName];
    const isLastEditedFieldInStage = lastEditedField
      ? stageFields.includes(lastEditedField)
      : false;

    const isCurrentStageSelected = isStepAble && nextStatus === stepName;
    if (caseId && (isCurrentStageSelected || isLastEditedFieldInStage)) {
      setIsOpen(true);
    }
  }, [caseId, nextStatus, isStepAble, stepName]);

  const collapseTitleButton: CollapseActionButton = button
    ? {
        ...button,
        onClick: (e) => {
          e.stopPropagation();
          setIsOpen(true);
          if (isAdvancingStage) {
            const stage = capitalize(t(`case:status.${stepName}`, { defaultValue: stepName }));
            const currentStage = capitalize(
              t(`case:status.${currentStatus}`, { defaultValue: currentStatus }),
            );
            return openModal(
              <DialogModal
                title={t('case:goToStage.title', { stage, currentStage })}
                message={t('case:goToStage.message', { stage, currentStage })}
                confirmButtonLabel={t('case:goToStage.yes', { stage })}
                cancelButtonLabel={t('case:goToStage.no', { currentStage })}
                onConfirm={() => {
                  setValue('status', stepName);
                  onSubmit();
                  button?.onClick?.(e);
                }}
              />,
            );
          }
          return button?.onClick?.(e);
        },
      }
    : {
        text: t('case:mandatory_fields', {
          status: t(`case:status.${stepName}`),
        }),
        visible: isStepAble && isOpen,
      };

  return (
    <Collapse
      className={`case-form-step-${stepName}`}
      title={
        <CollapseTitle
          title={t(`case:title.${stepName}`)}
          indicator={CaseStatusEnum[stepName].toString()}
          button={collapseTitleButton}
          disabled={!isStepAble && !isAdvancingStage}
          {...props}
        />
      }
      toggleIsOpen={() => setIsOpen(!isOpen)}
      isOpen={isOpen}
    >
      {formComponent({
        isStepRequired,
        isStepAble,
        isAdvancingStage,
        isCurrentStageSelected: stepName === nextStatus,
        isPreviousStepFilled,
        onSubmit,
      })}
    </Collapse>
  );
};

export default CaseFormStep;
